import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { LogoLockup } from "@/components/Logo";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";

export default function Poster() {
  const { slug = "ludus_gladius" } = useParams();
  const [academy, setAcademy] = useState({ name: "", established: "", members: "" });
  const url = `${window.location.origin}/student/login`;

  useEffect(() => {
    api.get(`/public/academy/${slug}`).then(r => setAcademy(r.data)).catch(()=>{});
  }, [slug]);

  const printPoster = () => window.print();

  return (
    <div data-testid="page-poster" style={{ background: "#0a0b0a", minHeight: "100vh" }}>
      {/* Print toolbar (hidden on print) */}
      <div className="no-print sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--border)", background: "rgba(10,11,10,0.85)" }}>
        <div className="label">Sign-in poster · A4 portrait</div>
        <button onClick={printPoster} className="btn btn-primary" data-testid="btn-print"><Printer size={14}/> Print / Save as PDF</button>
      </div>

      {/* Poster sheet — sized to A4 */}
      <div className="poster-wrap mx-auto p-8">
        <div className="poster" data-testid="poster-sheet">
          {/* Top brand band */}
          <div className="poster-top">
            <LogoLockup size={28} beltWidth={220} tagSize={10} />
          </div>

          {/* Headline */}
          <div className="poster-headline">
            <div className="poster-eyebrow">{academy.name}</div>
            <h1>Join the<br/><span style={{color:"var(--green)"}}>mats</span> on RollOSS.</h1>
            <p>Scan to sign up · reserve classes · track your training streak · watch your belt progression.</p>
          </div>

          {/* QR */}
          <div className="qr-frame">
            <div className="qr-corner tl"/><div className="qr-corner tr"/>
            <div className="qr-corner bl"/><div className="qr-corner br"/>
            <QRCodeSVG
              value={url}
              size={420}
              bgColor="#ffffff"
              fgColor="#0a0b0a"
              level="H"
              includeMargin={false}
              style={{ width: "100%", height: "100%", maxWidth: 420 }}
            />
          </div>

          {/* Footer */}
          <div className="poster-foot">
            <div>
              <div className="poster-foot-label">Scan with your phone camera</div>
              <div className="poster-foot-url">{url.replace("https://","")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="poster-foot-label">Academy</div>
              <div className="poster-foot-meta">{academy.name} · est. {academy.established}</div>
            </div>
          </div>

          {/* Install instructions — after sign-in, add to home screen */}
          <div className="poster-install">
            <div className="poster-install-col">
              <div className="poster-install-title">iPhone · Safari</div>
              <div className="poster-install-title-es">Toca “Compartir” → “Agregar a Inicio”.</div>
              <ol className="poster-install-list">
                <li>Open <b>getrolloss.app</b> in Safari</li>
                <li>Tap Share <span className="poster-glyph">⬆</span></li>
                <li>Tap “Add to Home Screen”</li>
              </ol>
            </div>
            <div className="poster-install-col">
              <div className="poster-install-title">Android · Chrome</div>
              <div className="poster-install-title-es">Toca ⋮ → “Instalar app”.</div>
              <ol className="poster-install-list">
                <li>Open <b>getrolloss.app</b> in Chrome</li>
                <li>Tap the <span className="poster-glyph">⋮</span> menu</li>
                <li>Tap “Install app”</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 0; }
        .poster-wrap { display: flex; justify-content: center; }
        .poster {
          width: 210mm; min-height: 297mm;
          background: #0a0b0a;
          color: #e8ece9;
          display: grid;
          grid-template-rows: auto auto 1fr auto auto;
          padding: 22mm 18mm;
          gap: 12mm;
          border: 1px solid var(--border);
          box-shadow: 0 30px 80px rgba(0,0,0,0.45);
        }
        .poster-top { display: flex; justify-content: center; }
        .poster-headline { text-align: center; }
        .poster-eyebrow {
          font-family: 'JetBrains Mono', monospace; font-size: 12px;
          letter-spacing: 0.32em; text-transform: uppercase; color: var(--green);
        }
        .poster-headline h1 {
          font-family: 'Geist', sans-serif; font-weight: 900;
          font-size: 60px; line-height: 0.98; letter-spacing: -0.04em;
          margin: 14px 0 0; text-transform: uppercase;
        }
        .poster-headline p {
          margin-top: 16px; font-size: 13px; line-height: 1.7;
          letter-spacing: 0.04em; color: var(--text-dim);
          max-width: 520px; margin-left: auto; margin-right: auto;
        }
        .qr-frame {
          position: relative; align-self: center; justify-self: center;
          padding: 28px; background: #ffffff; border-radius: 16px;
          width: 480px; height: 480px;
          display: flex; align-items: center; justify-content: center;
        }
        .qr-corner {
          position: absolute; width: 26px; height: 26px;
          border-color: var(--green);
        }
        .qr-corner.tl { top: -3px; left: -3px; border-top: 4px solid var(--green); border-left: 4px solid var(--green); border-top-left-radius: 8px; }
        .qr-corner.tr { top: -3px; right: -3px; border-top: 4px solid var(--green); border-right: 4px solid var(--green); border-top-right-radius: 8px; }
        .qr-corner.bl { bottom: -3px; left: -3px; border-bottom: 4px solid var(--green); border-left: 4px solid var(--green); border-bottom-left-radius: 8px; }
        .qr-corner.br { bottom: -3px; right: -3px; border-bottom: 4px solid var(--green); border-right: 4px solid var(--green); border-bottom-right-radius: 8px; }
        .poster-foot {
          display: flex; justify-content: space-between; align-items: flex-end;
          padding-top: 16px; border-top: 1px solid var(--border);
        }
        .poster-foot-label {
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          letter-spacing: 0.22em; text-transform: uppercase; color: var(--text-muted);
        }
        .poster-foot-url {
          font-family: 'JetBrains Mono', monospace; font-size: 13px;
          color: var(--green); margin-top: 4px;
        }
        .poster-foot-meta { font-size: 13px; margin-top: 4px; color: var(--text-dim); }

        /* Install instructions — bilingual EN/ES, print-friendly, two columns */
        .poster-install {
          display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
          padding-top: 14px; border-top: 1px solid var(--border);
        }
        .poster-install-col {
          padding: 12px 14px;
          border: 1px solid var(--border); border-radius: 8px;
          background: rgba(255,255,255,0.02);
        }
        .poster-install-title {
          font-family: 'JetBrains Mono', monospace; font-size: 11px;
          letter-spacing: 0.22em; text-transform: uppercase; color: var(--green);
        }
        .poster-install-title-es {
          font-size: 10.5px; margin-top: 4px; color: var(--text-dim); font-style: italic;
        }
        .poster-install-list {
          margin: 8px 0 0 16px; padding: 0;
          font-size: 10.5px; line-height: 1.55; color: #e8ece9;
        }
        .poster-install-list li { margin-bottom: 2px; }
        .poster-install-list b { color: var(--green); font-weight: 700; }
        .poster-glyph {
          display: inline-block; padding: 0 4px;
          border: 1px solid var(--border); border-radius: 4px;
          font-size: 11px; margin: 0 2px; color: var(--green);
        }

        /* Mobile preview — shrink the A4 sheet so it fits a phone screen. */
        @media (max-width: 768px) {
          .poster-wrap { padding: 12px; }
          .poster {
            width: 100%;
            min-height: auto;
            padding: 32px 22px;
            gap: 22px;
            box-shadow: 0 12px 32px rgba(0,0,0,0.4);
          }
          .poster-headline h1 { font-size: 38px; }
          .poster-headline p { font-size: 12px; max-width: 320px; }
          .qr-frame {
            width: min(78vw, 320px);
            height: min(78vw, 320px);
            padding: 16px;
          }
          .qr-frame svg { width: 100% !important; height: 100% !important; }
          .poster-foot { flex-direction: column; align-items: flex-start; gap: 12px; }
          .poster-foot > div:last-child { text-align: left !important; }
          .poster-foot-url { font-size: 12px; word-break: break-all; }
          .poster-install { grid-template-columns: 1fr; gap: 10px; }
        }

        @media print {
          .no-print { display: none !important; }
          body { background: #ffffff !important; }
          .poster-wrap { padding: 0; }
          .poster { border: 0; box-shadow: none; width: 210mm; height: 297mm; }
        }
      `}</style>
    </div>
  );
}
